import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  type ColumnSizingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Lightbox } from "@/components/Lightbox";
import { Tooltip } from "@/components/Tooltip";
import { CommentBody } from "@/components/CommentBody";
import { NewCommentDialog } from "@/components/NewCommentDialog";
import { ProductThumb } from "@/components/ProductThumb";
import { api, type ConditionGrade, type ProductKind, type ProductRow } from "@/lib/api";
import { READONLY } from "@/lib/config";
import { formatDate, formatMoney, formatMoneyWhole } from "@/lib/format";
import { Download, InfoCircle, Search, Sliders } from "@/lib/icons";
import { toast } from "sonner";

const ALL_KINDS = [
  "filament","resin","resin_additive","consumable","printer","printer_part",
  "3d_scanner","laser_engraver","forming_machine","post_processor",
  "lab_equipment","office_equipment","document","other",
] satisfies ProductKind[];

const GRADE_LABELS: Record<string, string> = {
  new: "New", excellent: "Excellent", good: "Good",
  used: "Used", demonstration: "Demonstration",
};

function PriceOverrideCell({ uid, value }: { uid: string; value: number | null }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string>(value != null ? (value / 100).toFixed(2) : "");
  useEffect(() => { setDraft(value != null ? (value / 100).toFixed(2) : ""); }, [value]);

  function commit(raw: string) {
    const trimmed = raw.trim();
    const cents = trimmed === "" ? null : Math.round(parseFloat(trimmed) * 100);
    if (cents !== null && (isNaN(cents) || cents < 0)) {
      setDraft(value != null ? (value / 100).toFixed(2) : "");
      return;
    }
    api.patchProduct(uid, { price_override_minor: cents })
      .then(() => qc.invalidateQueries({ queryKey: ["products"] }))
      .catch(() => {
        setDraft(value != null ? (value / 100).toFixed(2) : "");
        toast.error("Couldn't update price override");
      });
  }

  return (
    <input
      type="number"
      step="0.01"
      min="0"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.currentTarget.blur(); }
        if (e.key === "Escape") { setDraft(value != null ? (value / 100).toFixed(2) : ""); e.currentTarget.blur(); }
      }}
      placeholder="—"
      className={`w-full rounded border border-zinc-200 bg-white px-1 py-0.5 text-sm tabular-nums${draft !== "" ? " text-blue-600" : ""}`}
    />
  );
}

function GradeCell({ uid, grade, muted }: { uid: string; grade: ConditionGrade | null; muted: boolean }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<string>(grade ?? "new");
  useEffect(() => { setLocal(grade ?? "new"); }, [grade]);

  return (
    <select
      value={local}
      onChange={(e) => {
        const v = e.target.value;
        setLocal(v);
        api.patchProduct(uid, { condition_grade: (v || null) as ConditionGrade | null })
          .then(() => qc.invalidateQueries({ queryKey: ["products"] }))
          .catch(() => {
            setLocal(grade ?? "");
            toast.error("Couldn't update grade");
          });
      }}
      className={`w-full rounded border border-zinc-200 bg-white px-1 py-0.5 text-sm${muted ? " opacity-50" : ""}`}
    >
      <option value="">—</option>
      {Object.entries(GRADE_LABELS).map(([val, label]) => (
        <option key={val} value={val}>{label}</option>
      ))}
    </select>
  );
}

const COL_SIZING_KEY = "wb.products.colSizing";
const COL_VIS_KEY    = "wb.products.colVisibility.v2";

const DEFAULT_HIDDEN_COLS: VisibilityState = {
  ean_sku: false,
  color_material: false,
  batch: false,
  status: false,
  processed_at: false,
};

const PINNED_COLS = new Set(["price", "price_override", "adj_total", "condition_adj"]);

function calcAdjTotal(r: ProductRow, multipliers: Record<string, number>): number | null {
  if (r.price_override_minor != null)
    return Math.round(r.price_override_minor * r.total_quantity);
  const price = r.price_median;
  if (price == null) return null;
  const pct = r.condition_adj_enabled ? (multipliers[r.condition_grade ?? "new"] ?? 100) : 100;
  return Math.round(price * (pct / 100) * r.total_quantity);
}

function ConditionAdjCell({ uid, enabled }: { uid: string; enabled: 0 | 1 }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<0 | 1>(enabled);
  useEffect(() => { setLocal(enabled); }, [enabled]);

  return (
    <input
      type="checkbox"
      checked={local === 1}
      onChange={(e) => {
        const v: 0 | 1 = e.target.checked ? 1 : 0;
        setLocal(v);
        api.patchProduct(uid, { condition_adj_enabled: v })
          .then(() => qc.invalidateQueries({ queryKey: ["products"] }))
          .catch(() => {
            setLocal(enabled);
            toast.error("Couldn't update condition adjustment");
          });
      }}
      className="size-4 accent-indigo-600"
    />
  );
}

export function Products() {
  const [q, setQ]               = useState("");
  const [batch, setBatch]       = useState<string>("");
  const [brand, setBrand]       = useState<string>("");
  const [status, setStatus]     = useState<string>("");
  const [kinds, setKinds]       = useState<ProductKind[]>(ALL_KINDS);
  const [kindsOpen, setKindsOpen] = useState(false);
  const kindsRef = useRef<HTMLDivElement>(null);
  const [missingIds, setMissingIds] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [parentUid, setParentUid] = useState("");
  const isLocalhost = window.location.hostname === "localhost" ||
                      window.location.hostname === "127.0.0.1";
  const [sort, setSort]     = useState<string>("price");
  const [order, setOrder]   = useState<"asc" | "desc">("desc");
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  if (!isLocalhost && perPage === 0) setPerPage(50);
  const [lightbox, setLightbox] = useState<{ src: string; bbox?: string | null } | null>(null);
  const [commentTarget, setCommentTarget] = useState<{ uid: string; name: string } | null>(null);
  const [colsOpen, setColsOpen] = useState(false);
  const colsRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const DEFAULT_MULTIPLIERS = { new: 100, demonstration: 90, excellent: 80, good: 60, used: 40 };
  const [multipliers, setMultipliers] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem("wb_cond_multipliers");
      return raw ? { ...DEFAULT_MULTIPLIERS, ...JSON.parse(raw) } : DEFAULT_MULTIPLIERS;
    } catch { return DEFAULT_MULTIPLIERS; }
  });
  const [showCondPopover, setShowCondPopover] = useState(false);
  const condPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showCondPopover) return;
    function onDown(e: MouseEvent) {
      if (condPopoverRef.current && !condPopoverRef.current.contains(e.target as Node))
        setShowCondPopover(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showCondPopover]);

  useEffect(() => {
    if (!kindsOpen) return;
    function onDown(e: MouseEvent) {
      if (kindsRef.current && !kindsRef.current.contains(e.target as Node))
        setKindsOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [kindsOpen]);

  const restoreMutation = useMutation({
    mutationFn: (uid: string) => api.restore(uid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
    onError: () => toast.error("Couldn't restore this product. Please try again."),
  });

  const kindsKey = kinds.length === 0 || kinds.length === ALL_KINDS.length
    ? ""
    : [...kinds].sort().join(",");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["products", q, batch, brand, status, kindsKey, missingIds, showArchived, parentUid, sort, order, page, perPage,
               multipliers.new, multipliers.excellent, multipliers.good, multipliers.used, multipliers.demonstration],
    queryFn: () =>
      api.products({ q, batch, brand, status, missing_ids: missingIds ? "1" : undefined, archived: showArchived ? "1" : undefined, parent_uid: parentUid || undefined, sort, order, page, per_page: perPage,
        cond_new: multipliers.new, cond_excellent: multipliers.excellent, cond_good: multipliers.good,
        cond_used: multipliers.used, cond_demonstration: multipliers.demonstration },
        kinds.length === ALL_KINDS.length ? [] : kinds),
    enabled: kinds.length > 0,
    placeholderData: keepPreviousData,
  });

  const { data: batches } = useQuery({
    queryKey: ["batches"],
    queryFn: () => api.batchesSummary(),
  });

  const { data: brands } = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.brands(),
  });

  // close column picker on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (colsRef.current && !colsRef.current.contains(e.target as Node))
        setColsOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const columns = useMemo<ColumnDef<ProductRow>[]>(
    () => [
      {
        id: "photo",
        header: "Photo",
        size: 80,
        minSize: 60,
        cell: ({ row }) => {
          const r = row.original;
          if (!r.primary_photo_id) return <div className="size-12 rounded bg-zinc-100" />;
          return (
            <button
              type="button"
              onClick={() => setLightbox({ src: api.photoUrl(r.primary_photo_id!), bbox: r.bbox })}
              className="block overflow-hidden rounded ring-zinc-200 ring-offset-2 ring-offset-white transition-shadow hover:ring-2"
              aria-label="view full-size"
            >
              <ProductThumb photoId={r.primary_photo_id} bbox={r.bbox} size="lg" />
            </button>
          );
        },
      },
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        size: 280,
        minSize: 160,
        cell: ({ row }) => (
          <Link
            to={`/product/${row.original.uid}`}
            className="font-medium text-indigo-700 hover:underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      { id: "brand", header: "Brand", accessorKey: "brand", size: 140 },
      {
        id: "ean_sku",
        header: "EAN / SKU",
        size: 160,
        minSize: 110,
        cell: ({ row }) => {
          const r = row.original;
          if (!r.ean13 && !r.manufacturer_sku) return <span className="text-zinc-300">—</span>;
          return (
            <div className="leading-tight">
              {r.ean13 && <div className="font-mono text-xs tabular-nums">{r.ean13}</div>}
              {r.manufacturer_sku && (
                <div className="text-xs text-zinc-400">{r.manufacturer_sku}</div>
              )}
            </div>
          );
        },
      },
      {
        id: "color_material",
        header: "Colour/Material",
        accessorKey: "color_material",
        size: 260,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="flex items-center gap-1.5">
              {r.color_hex ? (
                <span
                  className="inline-block size-3 shrink-0 rounded-full ring-1 ring-zinc-200"
                  style={{ background: r.color_hex }}
                />
              ) : r.color_family ? (
                <span className="inline-block size-3 shrink-0 rounded-full bg-zinc-200 ring-1 ring-zinc-200" />
              ) : null}
              <span className="truncate">{r.color_material ?? "—"}</span>
            </div>
          );
        },
      },
      {
        id: "quantity",
        header: "Qty",
        size: 80,
        minSize: 60,
        accessorFn: (r) => r.total_quantity,
        cell: ({ row }) => {
          const r = row.original;
          if (r.total_quantity === 0 && r.quantity == null) return <span className="text-zinc-300">—</span>;
          return (
            <span className="tabular-nums">
              {r.total_quantity}
              {r.child_count > 0 && <span className="ml-1 text-xs text-zinc-400">({r.child_count})</span>}
            </span>
          );
        },
      },
      {
        id: "product_kind",
        header: "Kind",
        accessorKey: "product_kind",
        size: 110,
        cell: ({ getValue }) => {
          const v = getValue<string | null>();
          return v ? <KindBadge kind={v} /> : <span className="text-zinc-300">—</span>;
        },
      },
      {
        id: "condition_grade",
        header: "Condition",
        accessorKey: "condition_grade",
        size: 150,
        cell: ({ row: { original: r } }) => {
          const muted = !r.condition_adj_enabled;
          return READONLY
            ? <span className={`text-sm text-zinc-700${muted ? " opacity-50" : ""}`}>{GRADE_LABELS[r.condition_grade ?? "new"] ?? "New"}</span>
            : <GradeCell uid={r.uid} grade={r.condition_grade} muted={muted} />;
        },
      },
      {
        id: "condition_adj",
        meta: { label: "← $ adj" },
        header: () => (
          <Tooltip className="normal-case font-normal tracking-normal" width="w-72" content={
            <div className="space-y-0.5">
              <div>When <span className="font-semibold">checked</span>: condition grade % is applied to the price in Adj Total.</div>
              <div>When <span className="font-semibold">unchecked</span>: full price is used (100%), condition is ignored.</div>
              <div className="mt-1 border-t border-zinc-600 pt-1 text-zinc-400">Has no effect when an Override $ is set.</div>
            </div>
          }>
            ← $ adj
            <InfoCircle className="size-3 shrink-0 cursor-default text-zinc-400" strokeWidth={2.5} />
          </Tooltip>
        ),
        size: 90,
        minSize: 90,
        maxSize: 90,
        enableResizing: false,
        accessorKey: "condition_adj_enabled",
        cell: ({ row: { original: r } }) =>
          READONLY
            ? <input type="checkbox" checked={r.condition_adj_enabled === 1} disabled className="size-4" />
            : <ConditionAdjCell uid={r.uid} enabled={r.condition_adj_enabled} />,
      },
      { id: "batch", header: "Batch", accessorKey: "batch", size: 120 },
      {
        id: "price",
        header: "Mdn $",
        size: 120,
        minSize: 90,
        accessorFn: (r) => r.price_median,
        cell: ({ row }) => {
          const r = row.original;
          const muted = r.price_override_minor != null;
          if (r.price_median == null) return <span className="text-zinc-300">—</span>;
          const med = formatMoneyWhole(r.price_median, "AUD");
          if (r.price_count <= 1 || !r.price_half_range)
            return <span className={`tabular-nums${muted ? " text-zinc-300" : ""}`}>{med}</span>;
          const spread = formatMoneyWhole(r.price_half_range, "AUD");
          return (
            <span className={`tabular-nums${muted ? " text-zinc-300" : ""}`}>
              {med}
              <span className={`ml-1 text-xs${muted ? " text-zinc-300" : " text-zinc-500"}`}>±{spread}</span>
            </span>
          );
        },
      },
      {
        id: "price_override",
        header: "Override $",
        size: 120,
        minSize: 90,
        accessorKey: "price_override_minor",
        cell: ({ row: { original: r } }) =>
          READONLY
            ? <span className={`tabular-nums ${r.price_override_minor != null ? "text-blue-600" : "text-zinc-300"}`}>{r.price_override_minor != null ? formatMoney(r.price_override_minor, "AUD") : "—"}</span>
            : <PriceOverrideCell uid={r.uid} value={r.price_override_minor} />,
      },
      {
        id: "adj_total",
        meta: { label: "Adj Total" },
        size: 150,
        minSize: 110,
        accessorFn: (r) => calcAdjTotal(r, multipliers),
        header: () => (
          <Tooltip
            className="normal-case font-normal tracking-normal"
            width="w-72"
            content={
              <div className="space-y-0.5">
                <div><span className="text-zinc-400">Override $</span>: used as-is (condition % not applied)</div>
                <div><span className="text-zinc-400">Mdn $</span>: × condition % (from Value adjustments)</div>
                <div><span className="text-zinc-400">× quantity</span></div>
                <div className="mt-1 border-t border-zinc-600 pt-1 font-semibold">= Adjusted Total</div>
              </div>
            }
          >
            Adj Total
            <InfoCircle className="size-3 shrink-0 cursor-default text-zinc-400" strokeWidth={2.5} />
          </Tooltip>
        ),
        cell: ({ row }) => {
          const r = row.original;
          const hasOverride = r.price_override_minor != null;
          const price = hasOverride ? r.price_override_minor! : r.price_median;
          if (price == null) return <span className="text-zinc-300">—</span>;
          const condAdj = !hasOverride && !!r.condition_adj_enabled;
          const pct = condAdj ? (multipliers[r.condition_grade ?? "new"] ?? 100) : 100;
          const adj = Math.round(price * (pct / 100) * r.total_quantity);
          const gradeLabel = r.condition_grade ?? "new";
          return (
            <Tooltip
              className="tabular-nums"
              content={
                <div className="space-y-0.5">
                  <div><span className="font-semibold">{formatMoney(price, "AUD")}</span> <span className="text-zinc-400">({hasOverride ? "override price — condition % not applied" : "median price"})</span></div>
                  {!hasOverride && (
                    <div>
                      <span className="text-zinc-400">×</span>{" "}
                      <span className={`font-semibold${!condAdj ? " line-through opacity-60" : ""}`}>{multipliers[r.condition_grade ?? "new"] ?? 100}%</span>{" "}
                      <span className="text-zinc-400">({gradeLabel}){!condAdj ? " — adj disabled, using 100%" : ""}</span>
                    </div>
                  )}
                  <div><span className="text-zinc-400">×</span> <span className="font-semibold">{r.total_quantity}</span> <span className="text-zinc-400">unit{r.total_quantity !== 1 ? "s" : ""}</span></div>
                  <div className="mt-1 border-t border-zinc-600 pt-1"><span className="text-zinc-400">= </span><span className="font-semibold">{formatMoney(adj, "AUD")}</span></div>
                </div>
              }
            >
              {formatMoneyWhole(adj, "AUD")}
              <InfoCircle className="size-3 shrink-0 cursor-default text-zinc-400" strokeWidth={2.5} />
            </Tooltip>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        size: 130,
        cell: ({ getValue }) => <StatusBadge status={getValue<string>()} />,
      },
      {
        id: "processed_at",
        header: "Processed",
        accessorKey: "processed_at",
        size: 160,
        cell: ({ getValue }) => (
          <span className="text-xs text-zinc-500">
            {formatDate(getValue<string>())}
          </span>
        ),
      },
      {
        id: "comments",
        header: "Comments",
        size: 480,
        minSize: 160,
        cell: ({ row }) => {
          const r = row.original;
          const cs = r.comments;
          const addBtn = (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setCommentTarget({ uid: r.uid, name: r.name }); }}
              className="self-start rounded border border-zinc-200 px-1.5 py-0.5 text-xs text-zinc-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              + Add comment
            </button>
          );
          if (!cs.length) return addBtn ?? <span className="text-zinc-300">—</span>;
          return (
            <div className="flex flex-col gap-0.5">
              {cs.map((c) => (
                <p key={c.id} className="break-words text-xs leading-tight text-zinc-700">
                  <span
                    className="font-semibold"
                    style={c.username === "Claude" ? { color: "#DA7756" } : undefined}
                  >
                    {c.username}
                  </span>
                  {": "}
                  <CommentBody text={c.body} />
                </p>
              ))}
              <div className="mt-1">{addBtn}</div>
            </div>
          );
        },
      },
      {
        id: "restore",
        header: "",
        size: 90,
        cell: ({ row }) => (!READONLY && showArchived) ? (
          <button
            type="button"
            onClick={() => restoreMutation.mutate(row.original.uid)}
            className="rounded border border-zinc-200 bg-white px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50"
          >
            Restore
          </button>
        ) : null,
      },
    ],
    [showArchived, restoreMutation, multipliers],
  );

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() => {
    try { return JSON.parse(window.localStorage.getItem(COL_SIZING_KEY) ?? "{}"); }
    catch { return {}; }
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    try {
      window.localStorage.removeItem("wb.products.colVisibility");
      const saved = window.localStorage.getItem(COL_VIS_KEY);
      const base: VisibilityState = saved ? JSON.parse(saved) : { ...DEFAULT_HIDDEN_COLS };
      for (const id of PINNED_COLS) delete base[id];
      return base;
    } catch {
      return { ...DEFAULT_HIDDEN_COLS };
    }
  });

  useEffect(() => {
    window.localStorage.setItem(COL_SIZING_KEY, JSON.stringify(columnSizing));
  }, [columnSizing]);

  useEffect(() => {
    window.localStorage.setItem(COL_VIS_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const table = useReactTable({
    data: kinds.length === 0 ? [] : (data?.items ?? []),
    columns,
    state: { columnSizing, columnVisibility },
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    defaultColumn: { minSize: 50, size: 150, maxSize: 800 },
    getCoreRowModel: getCoreRowModel(),
  });

  const noKinds = kinds.length === 0;
  const total = noKinds ? 0 : (data?.total ?? 0);
  const pages = perPage === 0 ? 1 : Math.max(1, Math.ceil(total / perPage));
  const totalValueAud = noKinds ? null : (data?.total_value_aud ?? null);
  const condAdjusted = noKinds ? null : (data?.condition_adjusted_value_aud ?? null);

  const visibleColIds = useMemo(() => {
    const UI_ONLY = new Set(["photo", "restore"]);
    return columns
      .map((c) => c.id)
      .filter((id): id is string => id !== undefined && !UI_ONLY.has(id) && columnVisibility[id] !== false)
      .join(",");
  }, [columnVisibility, columns]);

  function toggleSort(col: string) {
    if (sort === col) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(col);
      setOrder("asc");
    }
    setPage(1);
  }

  async function handleExport() {
    try {
      const blob = await api.exportCsv({
        q, batch, status, sort, order,
        brand: brand || undefined,
        kind: kinds.length === 1 ? kinds[0] : undefined,
        missing_ids: missingIds ? "1" : undefined,
        archived: showArchived ? "1" : undefined,
        cols: visibleColIds || undefined,
      });
      const href = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href, download: "warehouse.csv" }).click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Export failed");
    }
  }

  return (
    <div className="bg-white">
      <div className="sticky top-0 z-20 -mx-4 border-b border-zinc-200 bg-white px-4 pt-3 pb-3 shadow-sm sm:-mx-6 sm:px-6">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2 sm:flex-nowrap sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold tracking-tight">Products</h1>
          <div className="relative" ref={condPopoverRef}>
            <button
              type="button"
              onClick={() => setShowCondPopover(v => !v)}
              className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm hover:bg-zinc-50"
              aria-label="Condition value adjustments"
              aria-expanded={showCondPopover}
            >
              <Sliders className="size-4" />
              Value adjustments
            </button>
            {showCondPopover && (
              <div className="absolute left-0 top-full z-30 mt-1 w-72 rounded-lg border border-zinc-200 bg-white p-4 shadow-lg">
                <p className="text-sm font-semibold text-zinc-800">Condition value adjustments</p>
                <p className="mt-1 mb-3 text-xs text-zinc-500">
                  Each grade reduces the estimated value by a percentage. Use this to estimate
                  what your stock is realistically worth — e.g. "Used" items might sell for 40%
                  of list price. Items with a Price Override ignore these percentages entirely.
                </p>
                {(["new","demonstration","excellent","good","used"] as const).map(grade => (
                  <label key={grade} className="mb-2 flex items-center justify-between gap-2 text-sm">
                    <span className="capitalize text-zinc-700">{grade}</span>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={multipliers[grade]}
                        onChange={(e) => {
                          const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                          const next = { ...multipliers, [grade]: v };
                          setMultipliers(next);
                          localStorage.setItem("wb_cond_multipliers", JSON.stringify(next));
                        }}
                        className="w-16 rounded border border-zinc-200 px-2 py-1 text-right text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      />
                      <span className="text-zinc-400">%</span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          {totalValueAud != null && totalValueAud > 0 && (
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="text-sm font-medium text-zinc-900">
                Median RRP ≈ {formatMoneyWhole(totalValueAud, "AUD")}
              </span>
              {condAdjusted != null && condAdjusted > 0 && (
                <span className="group relative flex flex-wrap items-center gap-1 text-xs text-blue-600">
                  Condition and override price adjusted ≈ {formatMoneyWhole(condAdjusted, "AUD")}
                  <InfoCircle className="h-4 w-4 shrink-0 cursor-default" strokeWidth={2.5} />
                  <span className="pointer-events-none absolute left-full top-0 ml-1.5 w-64 rounded-md bg-zinc-800 px-3 py-2 text-xs leading-snug text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                    Each product&apos;s price is taken from the <strong>Override $</strong> column
                    if one is set, otherwise the median market price is discounted by its condition
                    rating. Condition discounts can be tuned in <strong>Value adjustments</strong>.
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm hover:bg-zinc-50"
        >
          <Download className="size-4" />
          Export CSV
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2 py-1.5">
          <Search className="size-4 text-zinc-400" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search name / brand / notes"
            className="w-full bg-transparent text-sm outline-none sm:w-64"
          />
        </label>

        {isLocalhost && (
          <select
            value={batch}
            onChange={(e) => { setBatch(e.target.value); setPage(1); }}
            className="flex-1 min-w-[130px] rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm"
          >
            <option value="">all batches</option>
            {(batches ?? []).map((b) => (
              <option key={b.batch} value={b.batch}>{b.batch}</option>
            ))}
          </select>
        )}

        {isLocalhost && (
          <input
            value={parentUid}
            onChange={(e) => { setParentUid(e.target.value); setPage(1); }}
            placeholder="parent UID"
            className="flex-1 min-w-[160px] rounded-md border border-zinc-200 bg-white px-2 py-1.5 font-mono text-sm outline-none"
          />
        )}

        <select
          value={brand}
          onChange={(e) => { setBrand(e.target.value); setPage(1); }}
          className="flex-1 min-w-[130px] rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm"
        >
          <option value="">all brands</option>
          {(brands ?? []).map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>

        {isLocalhost && (
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="flex-1 min-w-[130px] rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm"
          >
            <option value="">all statuses</option>
            <option value="done">done</option>
            <option value="needs_review">needs review</option>
            <option value="error">error</option>
          </select>
        )}

        <div className="relative" ref={kindsRef}>
          <button
            type="button"
            onClick={() => setKindsOpen(o => !o)}
            className="min-w-[130px] rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm hover:bg-zinc-50"
          >
            {kinds.length === 0
              ? "no types"
              : kinds.length === ALL_KINDS.length
                ? "all types"
                : `${kinds.length} type${kinds.length > 1 ? "s" : ""}`}
          </button>
          {kindsOpen && (
            <div className="absolute left-0 top-full z-30 mt-1 min-w-[180px] rounded-md border border-zinc-200 bg-white shadow-md">
              <div className="flex gap-1 border-b border-zinc-100 p-2">
                <button
                  type="button"
                  onClick={() => { setKinds(ALL_KINDS); setPage(1); }}
                  className="flex-1 rounded px-2 py-1 text-xs hover:bg-zinc-50"
                >Select all</button>
                <button
                  type="button"
                  onClick={() => { setKinds([]); setPage(1); }}
                  className="flex-1 rounded px-2 py-1 text-xs hover:bg-zinc-50"
                >Clear all</button>
              </div>
              <div className="max-h-[60vh] overflow-y-auto p-2">
                {ALL_KINDS.map(k => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-zinc-50">
                    <input
                      type="checkbox"
                      checked={kinds.includes(k)}
                      onChange={() => {
                        setKinds(prev =>
                          prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]
                        );
                        setPage(1);
                      }}
                      className="accent-indigo-600"
                    />
                    {k.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Column visibility picker */}
        <div className="relative ml-auto" ref={colsRef}>
          <button
            type="button"
            onClick={() => setColsOpen((o) => !o)}
            className="rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm hover:bg-zinc-50"
          >
            Columns
          </button>
          {colsOpen && (
            <div className="absolute right-0 top-full z-30 mt-1 min-w-[160px] max-h-[70vh] overflow-y-auto rounded-md border border-zinc-200 bg-white p-2 shadow-md">
              {table.getAllLeafColumns().map((col) => {
                if (!col.columnDef.header) return null;
                const pinned = PINNED_COLS.has(col.id);
                return (
                  <label
                    key={col.id}
                    title={pinned ? "This column is always visible" : undefined}
                    className={`flex items-center gap-2 rounded px-2 py-1 text-sm ${pinned ? "pointer-events-none opacity-50" : "cursor-pointer hover:bg-zinc-50"}`}
                  >
                    <input
                      type="checkbox"
                      checked={col.getIsVisible()}
                      onChange={pinned ? () => {} : col.getToggleVisibilityHandler()}
                      disabled={pinned}
                      className="accent-indigo-600"
                    />
                    {typeof col.columnDef.header === "string"
                      ? col.columnDef.header
                      : (col.columnDef.meta as { label?: string })?.label ?? col.id}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <span className="text-xs text-zinc-500">
          {isFetching ? "loading… " : ""}
          {total.toLocaleString()} total
        </span>
      </div>

      {!READONLY && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-sm text-rose-800 select-none">
            <input
              type="checkbox"
              checked={missingIds}
              onChange={(e) => { setMissingIds(e.target.checked); setPage(1); }}
              className="accent-rose-600"
            />
            Missing EAN / SKU
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-sm text-rose-800 select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }}
              className="accent-rose-600"
            />
            Archived only
          </label>
        </div>
      )}

      <div className="hidden sm:block">
        <PaginationBar
          page={page}
          pages={pages}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(v) => { setPerPage(v); setPage(1); }}
          className="mb-2"
        />
      </div>
      </div>

      <div className="-mx-4 mt-4 overflow-auto border-y border-zinc-200 bg-white sm:mx-0 sm:rounded-lg sm:border">
        <table
          className="text-sm"
          style={{ width: table.getCenterTotalSize(), tableLayout: "fixed" }}
        >
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = [
                    "name", "brand", "color_material", "quantity",
                    "batch", "processed_at", "price",
                  ].includes(h.column.id);
                  return (
                    <th
                      key={h.id}
                      style={{ width: h.getSize() }}
                      className="group relative select-none py-2 pl-2 pr-1.5 sm:px-3"
                    >
                      <button
                        type="button"
                        disabled={!sortable}
                        onClick={() => sortable && toggleSort(h.column.id)}
                        className={[
                          "inline-flex w-full items-center gap-1 text-left",
                          sortable ? "cursor-pointer hover:text-zinc-800" : "cursor-default",
                        ].join(" ")}
                      >
                        <span className="truncate">
                          {flexRender(h.column.columnDef.header, h.getContext())}
                        </span>
                        {sort === h.column.id ? (order === "asc" ? "▲" : "▼") : ""}
                      </button>
                      <div
                        onMouseDown={h.getResizeHandler()}
                        onTouchStart={h.getResizeHandler()}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={() => h.column.resetSize()}
                        title="drag to resize · double-click to reset"
                        className={[
                          "absolute right-0 top-0 h-full w-1.5 cursor-col-resize touch-none select-none",
                          "opacity-0 transition-opacity group-hover:opacity-100",
                          h.column.getIsResizing() ? "bg-indigo-500 opacity-100" : "bg-zinc-300",
                        ].join(" ")}
                      />
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className="overflow-hidden py-2 pl-2 pr-1.5 align-middle sm:px-3"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {!isLoading && !noKinds && (data?.items.length ?? 0) === 0 && (
              <tr>
                <td colSpan={table.getVisibleLeafColumns().length} className="px-3 py-12 text-center text-zinc-500">
                  No products yet. Run <code className="rounded bg-zinc-100 px-1.5 py-0.5">/catalogue-photos</code> to populate.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={page}
        pages={pages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={(v) => { setPerPage(v); setPage(1); }}
        className="mt-4"
      />

      <Lightbox src={lightbox?.src ?? null} bbox={lightbox?.bbox} onClose={() => setLightbox(null)} />
      <NewCommentDialog
        uid={commentTarget?.uid ?? ""}
        productName={commentTarget?.name ?? ""}
        open={commentTarget !== null}
        onClose={() => setCommentTarget(null)}
      />
    </div>
  );
}

const _isLocalhost = typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const PER_PAGE_OPTIONS = [
  { label: "25",  value: 25  },
  { label: "50",  value: 50  },
  { label: "100", value: 100 },
  { label: "500", value: 500 },
  ...(_isLocalhost ? [{ label: "All", value: 0 }] : []),
];

interface PaginationBarProps {
  page: number;
  pages: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  className?: string;
}

function PaginationBar({ page, pages, perPage, onPageChange, onPerPageChange, className }: PaginationBarProps) {
  return (
    <div className={`flex items-center justify-between text-xs text-zinc-500 ${className ?? ""}`}>
      <label className="flex items-center gap-1.5">
        <span className="hidden sm:inline">Show</span>
        <select
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
          className="rounded border border-zinc-200 bg-white px-1.5 py-1 text-xs text-zinc-700"
        >
          {PER_PAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span>per page</span>
      </label>
      <div className="flex items-center gap-2">
        <span>page {page} / {pages}</span>
        <button
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          className="min-h-[44px] rounded border border-zinc-200 bg-white px-4 disabled:opacity-50"
        >
          prev
        </button>
        <button
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
          className="min-h-[44px] rounded border border-zinc-200 bg-white px-4 disabled:opacity-50"
        >
          next
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    done:         "bg-emerald-50 text-emerald-700 ring-emerald-200",
    needs_review: "bg-amber-50 text-amber-700 ring-amber-200",
    error:        "bg-rose-50 text-rose-700 ring-rose-200",
  };
  const cls = map[status] ?? "bg-zinc-100 text-zinc-700 ring-zinc-200";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function KindBadge({ kind }: { kind: string }) {
  const map: Record<string, string> = {
    filament:        "bg-blue-50 text-blue-700 ring-blue-200",
    resin:           "bg-cyan-50 text-cyan-700 ring-cyan-200",
    resin_additive:  "bg-teal-50 text-teal-700 ring-teal-200",
    consumable:      "bg-yellow-50 text-yellow-700 ring-yellow-200",
    printer:         "bg-violet-50 text-violet-700 ring-violet-200",
    printer_part:    "bg-orange-50 text-orange-700 ring-orange-200",
    "3d_scanner":    "bg-indigo-50 text-indigo-700 ring-indigo-200",
    laser_engraver:  "bg-red-50 text-red-700 ring-red-200",
    forming_machine: "bg-pink-50 text-pink-700 ring-pink-200",
    post_processor:  "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
    lab_equipment:   "bg-emerald-50 text-emerald-700 ring-emerald-200",
    office_equipment:"bg-slate-50 text-slate-600 ring-slate-200",
    document:        "bg-stone-50 text-stone-600 ring-stone-200",
    other:           "bg-zinc-100 text-zinc-600 ring-zinc-200",
  };
  const label: Record<string, string> = {
    filament: "filament", resin: "resin", resin_additive: "resin additive",
    consumable: "consumable", printer: "printer", printer_part: "part",
    "3d_scanner": "scanner", laser_engraver: "laser", forming_machine: "forming",
    post_processor: "post-proc", lab_equipment: "lab", office_equipment: "office",
    document: "document", other: "other",
  };
  const cls = map[kind] ?? "bg-zinc-100 text-zinc-600 ring-zinc-200";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {label[kind] ?? kind}
    </span>
  );
}
